import {
  MutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {
  Client,
  InvitedOrganizationMember,
  queryCreate,
  queryDelete,
  queryGet,
} from '@app/api';
import { useOrganization, useCurrentOrganizationId } from '@app/api';
import { useTrackInvite } from '@app/analytics';

export const useCurrentOrganization = (allowAnonymous?: boolean) => {
  const id = useCurrentOrganizationId(allowAnonymous);
  return useOrganization(id);
};

export const useCurrentOrganizationMembers = (allowAnonymous?: boolean) => {
  const id = useCurrentOrganizationId(allowAnonymous);
  return useOrganizationMembers(id);
};

export const useOrganizationMembers = (
  organizationId?: string,
  { retry }: { retry?: boolean | number } = {},
) => {
  const queryClient = useQueryClient();
  return useQuery<Client[]>(
    ['organizationsMembers', organizationId],
    queryGet(`/organizations/${organizationId}/members`),
    {
      enabled: !!organizationId,
      retry,
      onSuccess: (clients: Client[]) => {
        clients.forEach(({ account }) => {
          queryClient.setQueryData(['accounts', account.id], account);
        });
      },
    },
  );
};

export const useInviteMemberToOrganization = () => {
  const id = useCurrentOrganizationId();
  const trackInvite = useTrackInvite();
  const queryClient = useQueryClient();
  return useMutation<string, any, any>(
    queryCreate(`/v2/organizations/${id}/invitations`),
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries('organizationsInvitedMembers');
        if (id) {
          trackInvite({
            email: data.email,
            id: data.id,
            role: 'member',
          });
        }
      },
    },
  );
};

export const useReinviteMemberToOrganization = (
  options?: MutationOptions<string, any, any>,
) => {
  const id = useCurrentOrganizationId();
  const queryClient = useQueryClient();
  return useMutation<string, any, any>(
    queryCreate(`/v2/organizations/${id}/resend-invitations`),
    {
      ...options,
      onSuccess: (data: string, variables: any, context: any | undefined) => {
        queryClient.invalidateQueries('organizationsInvitedMembers');
        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
      },
    },
  );
};

export const useRemoveInvitation = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, any, void>(
    queryDelete(`/v2/organizations/invitations/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('organizationsInvitedMembers');
      },
    },
  );
};

export const useRemoveAccountFromOrganization = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, any, { accountId: string }>(
    queryDelete(`/v2/organizations/${id}/accounts`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organizationsMembers', id]);
      },
    },
  );
};

export const useCurrentOrganizationInvitedMembers = (
  allowAnonymous?: boolean,
) => {
  const id = useCurrentOrganizationId(allowAnonymous);

  return useQuery<InvitedOrganizationMember[]>(
    ['organizationsInvitedMembers', id],
    queryGet(`/v2/organizations/${id}/invitations`),
    {
      enabled: !!id,
    },
  );
};
